/* ============================================================================
   UI / datePicker — choosing a day.
   DatePicker: a button that shows the chosen day and opens a small month. Days before `min` cannot be picked, and a week
   with no day that can be picked is not drawn at all (the browser's own date box always draws the whole month).
   ============================================================================ */
import React, { useEffect, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Button, IconButton } from "./actions.jsx";
import { useMenu } from "./menu.jsx";
import { cx } from "./util.js";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const toKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const fromKey = (key) => {
  if (!key) return null;
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
};
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

/* The days of the month in view as rows of seven, with the weeks wholly outside it and the weeks with nothing to pick left out. */
function monthDays(view, min) {
  const gridStart = new Date(view);
  gridStart.setDate(1 - view.getDay());
  const days = [];
  for (let week = 0; week < 6; week += 1) {
    const row = Array.from({ length: 7 }, (_, d) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + week * 7 + d));
    if (row.every((day) => day.getMonth() !== view.getMonth())) continue;
    if (min && row.every((day) => toKey(day) < min)) continue;
    days.push(...row);
  }
  return days;
}

/* value and min are "YYYY-MM-DD" (or empty); onChange(day) gets "YYYY-MM-DD", or "" when the person clears it. `label` is
   the button's name for screen readers. */
export function DatePicker({ value, min, onChange, label, placeholder = "Set date", className }) {
  const { open, setOpen, alignRight, root, trigger, menu } = useMenu();
  const minDate = fromKey(min) || new Date();
  const [view, setView] = useState(() => startOfMonth(fromKey(value) || minDate));
  useEffect(() => {
    if (open) setView(startOfMonth(fromKey(value) || fromKey(min) || new Date()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const today = toKey(new Date());
  const canGoBack = !min || view.getFullYear() > minDate.getFullYear() || (view.getFullYear() === minDate.getFullYear() && view.getMonth() > minDate.getMonth());
  const step = (months) => setView(new Date(view.getFullYear(), view.getMonth() + months, 1));
  const pick = (key) => { onChange(key); setOpen(false); };
  const chosen = fromKey(value);

  return (
    <span className={cx("ds-datepicker", className)} ref={root}>
      <Button type="button" size="sm" ref={trigger} className={cx("ds-datepicker__btn", !value && "is-empty")} leftIcon={<Calendar size={14} />}
        aria-label={label} aria-haspopup="dialog" aria-expanded={open} title="Click to pick a date" onClick={() => setOpen((v) => !v)}>
        {chosen ? chosen.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : placeholder}
      </Button>
      {open && (
        <div ref={menu} role="dialog" aria-label="Choose date" className={cx("ds-datepicker__pop", alignRight && "is-right")}>
          <div className="ds-datepicker__head">
            <IconButton label="Previous month" disabled={!canGoBack} onClick={() => step(-1)}><ChevronLeft size={16} /></IconButton>
            <strong>{view.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</strong>
            <IconButton label="Next month" onClick={() => step(1)}><ChevronRight size={16} /></IconButton>
          </div>
          <div className="ds-datepicker__grid">
            {WEEKDAYS.map((day) => <span key={day} className="ds-datepicker__dow">{day}</span>)}
            {monthDays(view, min).map((day) => {
              const key = toKey(day);
              return (
                <button key={key} type="button" disabled={Boolean(min) && key < min} aria-current={key === today ? "date" : undefined}
                  className={cx("ds-datepicker__day", value === key && "is-selected", day.getMonth() !== view.getMonth() && "is-outside")} onClick={() => pick(key)}>
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <div className="ds-datepicker__foot">
            <Button type="button" variant="link" size="sm" onClick={() => pick("")}>Clear</Button>
            <Button type="button" variant="link" size="sm" onClick={() => pick(min || today)}>Today</Button>
          </div>
        </div>
      )}
    </span>
  );
}
